import React from 'react';
import { Search, UserPlus, Phone, Mail, MapPin } from 'lucide-react';

const statusBadge = (status) => {
  const map = {
    active: 'bg-green-100 text-green-700',
    pending: 'bg-amber-100 text-amber-700',
    inactive: 'bg-slate-100 text-slate-600',
    rejected: 'bg-red-100 text-red-600',
  };
  return map[status] || map.inactive;
};

const MemberList = ({ members, filters, setFilters, statusMap, isLoading, onMemberClick, onAddMember }) => {
  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Address', 'Postcode', 'Status', 'Contribution', 'Join Date'];
    const csvData = members.map(member => [
      member.name, member.email, member.phone, member.address, member.postcode,
      statusMap[member.status], `£${member.subscriptionAmount || member.contribution}`,
      member.joinDate ? new Date(member.joinDate).toLocaleDateString() : ''
    ]);
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field || ''}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `members-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* Gradient header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-4 sm:px-6 py-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            <span className="font-semibold text-base">Member Directory ({members.length})</span>
          </div>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <select
              value={filters.status}
              onChange={e => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-1.5 bg-white/20 border border-white/30 text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-white/50">
              <option value="all" className="text-slate-800">All Statuses</option>
              {Object.keys(statusMap).map(k => <option key={k} value={k} className="text-slate-800">{statusMap[k]}</option>)}
            </select>
            <div className="relative flex-1 sm:w-52">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-white/70" />
              <input
                type="text"
                placeholder="Search members…"
                value={filters.search}
                onChange={e => setFilters({ ...filters, search: e.target.value })}
                className="w-full pl-8 pr-3 py-1.5 bg-white/20 border border-white/30 text-white placeholder-white/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-white/50"
              />
            </div>
            <button onClick={exportToCSV}
              className="px-3 py-1.5 bg-white/20 border border-white/30 text-white hover:bg-white/30 rounded-lg text-sm font-medium transition-colors flex-shrink-0">
              Export CSV
            </button>
{onAddMember && (
              <button onClick={onAddMember}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-medium transition-colors flex-shrink-0">
                <UserPlus className="w-4 h-4" />
                <span className="hidden sm:inline">Add Member</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Card grid */}
      <div className="p-4 sm:p-6">
        {isLoading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-400 text-sm">Loading members…</p>
          </div>
        ) : members.length === 0 ? (
          <div className="text-center py-16 text-slate-400 text-sm">No members found</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {members.map(member => (
              <div key={member.id}
                onClick={() => onMemberClick && onMemberClick(member)}
                className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-md hover:border-blue-200 cursor-pointer transition-all duration-200 flex flex-col gap-3">
                {/* Top row */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">
                      {member.name?.split(' ').map(n => n[0]).slice(0, 2).join('')}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 text-sm truncate">{member.name}</p>
                      <p className="text-xs text-slate-400">{member.membershipRef || '—'}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${statusBadge(member.status)}`}>
                    {statusMap[member.status]}
                  </span>
                </div>

                {/* Contact */}
                <div className="space-y-1">
                  {member.email && (
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Mail className="w-3 h-3 flex-shrink-0" /><span className="truncate">{member.email}</span>
                    </div>
                  )}
                  {member.phone && (
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <Phone className="w-3 h-3 flex-shrink-0" /><span>{member.phone}</span>
                    </div>
                  )}
                  {member.postcode && (
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <MapPin className="w-3 h-3 flex-shrink-0" /><span>{member.postcode}</span>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="mt-auto pt-3 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-sm font-semibold text-green-600">£{member.subscriptionAmount || member.contribution || 0}/mo</span>
                  <span className="text-xs text-blue-600 font-medium">View Profile →</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberList;
